import type { GetBackTestResponse } from 'apiConnector/back-test';
import type { DateMode, ExpandedData } from 'state/backTest/backTest.model';
import type { HttpErrorType } from 'utils/http/httpClient.model';

export type BackTestActions =
  | CreateBackTest
  | SetSecondBackTestQuoteId
  | RemoveSecondBackTestQuoteId
  | RunBackTest
  | CancelBackTest
  | CancelBackTestSuccess
  | CancelBackTestFailure
  | RunBackTestSucceed
  | RunBackTestFailed
  | GetBackTest
  | GetBackTestSucceed
  | GetBackTestFailed
  | SetMaturities
  | SetMaturitiesMode
  | ResetBackTestResults
  | SetBackTestExpandedSection
  | SetSecondBackTestQuoteLabel
  | SetInitialBackTestQuoteLabel;

export function createBackTest(tabId: string, quoteId: string) {
  return {
    type: 'CREATE_BACK_TEST' as const,
    tabId,
    quoteId,
  };
}
export type CreateBackTest = ReturnType<typeof createBackTest>;

export function setSecondBackTestQuoteId(tabId: string, quoteId?: string | null) {
  return {
    type: 'SET_SECOND_BACK_TEST_QUOTE_ID' as const,
    tabId,
    quoteId,
  };
}
export type SetSecondBackTestQuoteId = ReturnType<typeof setSecondBackTestQuoteId>;

export function setSecondBackTestQuoteLabel(tabId: string, quoteLabel?: string) {
  return {
    type: 'SET_SECOND_BACK_TEST_QUOTE_LABEL' as const,
    tabId,
    quoteLabel,
  };
}
export type SetSecondBackTestQuoteLabel = ReturnType<typeof setSecondBackTestQuoteLabel>;

export function setInitialBackTestQuoteLabel(tabId: string, quoteLabel?: string) {
  return {
    type: 'SET_INITIAL_BACK_TEST_QUOTE_LABEL' as const,
    tabId,
    quoteLabel,
  };
}
export type SetInitialBackTestQuoteLabel = ReturnType<typeof setInitialBackTestQuoteLabel>;

export function removeSecondBackTestQuoteId(tabId: string) {
  return {
    type: 'REMOVE_SECOND_BACK_TEST_QUOTE_ID' as const,
    tabId,
  };
}
export type RemoveSecondBackTestQuoteId = ReturnType<typeof removeSecondBackTestQuoteId>;

export function runBackTest(tabId: string) {
  return {
    type: 'RUN_BACK_TEST' as const,
    tabId,
  };
}
export type RunBackTest = ReturnType<typeof runBackTest>;

export function runBackTestFailed(tabId: string, errorMessage?: string, timeout?: number, errorType?: HttpErrorType) {
  return {
    type: 'RUN_BACK_TEST_FAILED' as const,
    tabId,
    errorMessage,
    timeout,
    errorType,
  };
}
export type RunBackTestFailed = ReturnType<typeof runBackTestFailed>;

export function runBackTestSucceed(tabId: string, backTestId: string) {
  return {
    type: 'RUN_BACK_TEST_SUCCEED' as const,
    tabId,
    backTestId,
  };
}
export type RunBackTestSucceed = ReturnType<typeof runBackTestSucceed>;

export function getBackTest(tabId: string, backTestId: string) {
  return {
    type: 'GET_BACK_TEST' as const,
    tabId,
    backTestId,
  };
}
export type GetBackTest = ReturnType<typeof getBackTest>;

export function cancelBackTest(tabId: string) {
  return {
    type: 'CANCEL_BACK_TEST' as const,
    tabId,
  };
}
export type CancelBackTest = ReturnType<typeof cancelBackTest>;

export function cancelBackTestSuccess(tabId: string) {
  return {
    type: 'CANCEL_BACK_TEST_SUCCESS' as const,
    tabId,
  };
}
export type CancelBackTestSuccess = ReturnType<typeof cancelBackTestSuccess>;

export function cancelBackTestFailure(tabId: string) {
  return {
    type: 'CANCEL_BACK_TEST_FAILURE' as const,
    tabId,
  };
}
export type CancelBackTestFailure = ReturnType<typeof cancelBackTestFailure>;

export function getBackTestSucceed(tabId: string, response: GetBackTestResponse) {
  return {
    type: 'GET_BACK_TEST_SUCCEEDED' as const,
    tabId,
    response,
  };
}
export type GetBackTestSucceed = ReturnType<typeof getBackTestSucceed>;

export function getBackTestFailed(tabId: string, errorMessage?: string, timeout?: number, errorType?: HttpErrorType) {
  return {
    type: 'GET_BACK_TEST_FAILED' as const,
    tabId,
    errorMessage,
    timeout,
    errorType,
  };
}
export type GetBackTestFailed = ReturnType<typeof getBackTestFailed>;

export function setMaturities(tabId: string, maturities: readonly string[]) {
  return {
    type: 'SET_BACK_TEST_MATURITIES' as const,
    tabId,
    maturities,
  };
}
export type SetMaturities = ReturnType<typeof setMaturities>;

export function setBackTestMaturitiesMode(tabId: string, mode: DateMode) {
  return {
    type: 'SET_BACK_TEST_MATURITIES_MODE' as const,
    tabId,
    mode,
  };
}
export type SetMaturitiesMode = ReturnType<typeof setBackTestMaturitiesMode>;

export function resetBackTest(tabId: string) {
  return {
    type: 'RESET_BACK_TEST' as const,
    tabId,
  };
}
export type ResetBackTestResults = ReturnType<typeof resetBackTest>;

export function setBackTestExpandedSection(tabId: string, section: keyof ExpandedData, isExpanded: boolean) {
  return {
    type: 'SET_BACK_TEST_EXPANDED_SECTION' as const,
    tabId,
    section,
    isExpanded,
  };
}
export type SetBackTestExpandedSection = ReturnType<typeof setBackTestExpandedSection>;
